import { AuthInfo, Kiosk, Launcher } from '../types';
import {
  applyDocumentClasses,
  inject3rdPartyScript,
  trackAppLaunch,
} from '../support';
import { get, isString, omit } from 'lodash';

import { State } from '../types';
import Vue from 'vue';
import Vuex from 'vuex';
import http from '../services/http';
import router from '../router';

Vue.use(Vuex);

const KILL_APP_DELAY = 750;

export default new Vuex.Store({
  state: {
    kiosk: undefined,
    brand: undefined,
    store: undefined,
    group: undefined,
    launchers: [],
    launcher: undefined,
    launched: false,
    loading: true,
    active: false,
  } as State,
  getters: {
    hasKioskInfo: (state): boolean => state.kiosk !== undefined,
    primaryApp: (state): Launcher | undefined =>
      (state.launchers || []).find((launcher: Launcher) => launcher.primary),
    otherApps: (state): Launcher[] =>
      (state.launchers || []).filter((launcher: Launcher) => !launcher.primary),
    screenSaverURL: (state): string | undefined => {
      const screenSaverURL = state.kiosk?.screen_saver_url;

      if (!isString(screenSaverURL) || screenSaverURL.length === 0) {
        return undefined;
      }

      return screenSaverURL;
    },
    hasScreenSaver: (_, getters): boolean =>
      getters.screenSaverURL !== undefined,
  },
  mutations: {
    initialize(state, data: AuthInfo) {
      state.kiosk = omit(data, ['store', 'departments', 'launchers']) as Kiosk;
      state.launchers = get(data, 'launchers.data', []);
      state.store = omit(data.store, ['brand', 'group']);
      state.brand = data.store.brand;
      state.group = data.store.group;
    },
    setActive(state, isActive = true) {
      state.active = isActive;
    },
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setLauncher(state, launcher: Launcher | undefined = undefined) {
      state.launcher = launcher;
    },
    setLaunched(state, launched: boolean) {
      state.launched = launched;
    },
  },
  actions: {
    async initialize({ commit, getters }) {
      let data;

      commit('setLoading', true);

      try {
        await http.get(process.env.VUE_APP_HEARTBEAT_URL);

        const response = await http.get(process.env.VUE_APP_AUTH_URL);

        data = response.data;
      } catch (error) {
        commit('setLoading', false);

        const serviceUnavailableRoute = '/service-unavailable';

        if (Number(get(error, 'response.status')) === 401) {
          window.location.href = process.env.VUE_APP_REAUTH_URL;
        } else if (router.currentRoute.path !== serviceUnavailableRoute) {
          // go to the service-unavailable page if we're not already there
          router.replace({ path: serviceUnavailableRoute });
        }

        return;
      }

      if (data.launchers.length === 0) {
        window.location.href = process.env.VUE_APP_WEBAPP_URL;
        return;
      }

      commit('initialize', data);
      commit('setActive', !getters.hasScreenSaver);
      commit('setLoading', false);
      applyDocumentClasses();
    },
    resetDueToInactivity({ dispatch, commit, getters }) {
      commit('setActive', !getters.hasScreenSaver);
      dispatch('reset');
    },
    reset({ commit, state }) {
      commit('setLaunched', false);

      if (state.launcher) {
        inject3rdPartyScript(state.launcher.close_script_url);
      }

      setTimeout(() => commit('setLauncher'), KILL_APP_DELAY);
    },
    reboot() {
      this.dispatch('reset');
      http
        .get(process.env.VUE_APP_HEARTBEAT_URL)
        .then(() => (window.location.href = '/'))
        .catch(() =>
          router.push({
            path: '/service-unavailable',
            query: { t: String(Number(new Date())) },
          })
        );
    },
    async launch({ commit, state }, launcher) {
      commit('setLauncher', launcher);
      commit('setLaunched', true);

      if (!launcher || !state.store || !state.group || !state.kiosk) {
        throw new Error(
          'Cannot track app launch due to missing state information'
        );
      }

      trackAppLaunch(
        launcher.name,
        state.store.wakefern_id,
        state.store.name,
        state.group.name,
        state.kiosk.username
      );

      inject3rdPartyScript(launcher.open_script_url);
    },
  },
  modules: {},
});
